import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import GoogleSheetsService from '../services/GoogleSheetsService';

interface GoogleSheetsAvailabilityImportProps {
  onImportComplete: () => void;
  selectedSemesterId: number | null;
}

interface FamilyRecord {
  id: number;
  email: string;
  combined_name: string;
  first_name: string;
  last_name: string;
}

interface RowMatchInfo {
  familyId: number | null;
  matchType: 'email' | 'name' | 'new';
}

const sheetsService = GoogleSheetsService.getInstance();

const STORAGE_KEY = 'googleSheetAvailabilityUrl';

// Column mapping for the family availability form:
// 0: Timestamp
// 1: Current Semester (Fall/Spring) and Year
// 2: What is your first and last name?
// 3: What is your email address?
// 4: How many students would you like to host?
// 5: (empty column in sheet)
// 6: Additional requests or comments

export const GoogleSheetsAvailabilityImport: React.FC<GoogleSheetsAvailabilityImportProps> = ({
  onImportComplete,
  selectedSemesterId,
}) => {
  const [sheetUrl, setSheetUrl] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [lastN, setLastN] = useState(20);
  const [rows, setRows] = useState<string[][]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [status, setStatus] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [families, setFamilies] = useState<FamilyRecord[]>([]);
  const [rowMatches, setRowMatches] = useState<RowMatchInfo[]>([]);

  useEffect(() => {
    setIsAuthenticated(sheetsService.isSignedIn());
  }, []);

  const handleUrlChange = (url: string) => {
    setSheetUrl(url);
    localStorage.setItem(STORAGE_KEY, url);
  };

  const handleAuth = async () => {
    setStatus('Connecting to Google Sheets...');
    try {
      const success = await sheetsService.authenticate();
      setIsAuthenticated(success);
      setStatus(success ? 'Connected to Google Sheets.' : 'Authentication failed.');
    } catch (err: any) {
      setStatus(`Auth error: ${err.message}`);
    }
  };

  function matchFamily(row: string[], familyList: FamilyRecord[]): RowMatchInfo {
    const email = (row[3] || '').toLowerCase().trim();
    const name = (row[2] || '').trim();

    // Try email match first
    if (email) {
      const match = familyList.find(f => f.email.toLowerCase().trim() === email);
      if (match) return { familyId: match.id, matchType: 'email' };
    }

    // Try name match against combined_name
    if (name) {
      const nameLower = name.toLowerCase();
      const match = familyList.find(f =>
        f.combined_name.toLowerCase() === nameLower ||
        `${f.first_name} ${f.last_name}`.toLowerCase() === nameLower ||
        `${f.last_name}, ${f.first_name}`.toLowerCase() === nameLower
      );
      if (match) return { familyId: match.id, matchType: 'name' };
    }

    return { familyId: null, matchType: 'new' };
  }

  const handleFetch = async () => {
    const spreadsheetId = sheetsService.parseSpreadsheetId(sheetUrl);
    if (!spreadsheetId) {
      setStatus('Invalid Google Sheets URL. Please paste the full URL from your browser.');
      return;
    }

    setIsFetching(true);
    setStatus('Fetching rows...');
    try {
      const [fetched, familiesResult] = await Promise.all([
        sheetsService.fetchRows(spreadsheetId, 'Form Responses 1', lastN),
        supabase.from('families').select('id, email, combined_name, first_name, last_name'),
      ]);

      const familyList: FamilyRecord[] = (familiesResult.data || []) as FamilyRecord[];
      setFamilies(familyList);

      const matches = fetched.map(row => matchFamily(row, familyList));
      setRowMatches(matches);

      setRows(fetched);
      setSelected(new Set(fetched.map((_, i) => i)));

      const newCount = matches.filter(m => m.matchType === 'new').length;
      setStatus(
        `Fetched ${fetched.length} rows.` +
        (newCount > 0 ? ` ${newCount} new families will be created.` : '')
      );
    } catch (err: any) {
      setStatus(`Fetch error: ${err.message}`);
      setRows([]);
    } finally {
      setIsFetching(false);
    }
  };

  const toggleRow = (index: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === rows.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(rows.map((_, i) => i)));
    }
  };

  const handleImport = async () => {
    if (!selectedSemesterId) {
      setStatus('Error: No semester selected. Please select a semester in the header.');
      return;
    }

    const selectedIndices = Array.from(selected);
    const selectedRows = selectedIndices.map(i => rows[i]);
    const selectedMatches = selectedIndices.map(i => rowMatches[i]);

    if (selectedRows.length === 0) {
      setStatus('No rows selected.');
      return;
    }

    setIsImporting(true);
    setStatus(`Importing availability for ${selectedRows.length} families...`);
    try {
      let created = 0;
      let updated = 0;

      for (let idx = 0; idx < selectedRows.length; idx++) {
        const row = selectedRows[idx];
        const match = selectedMatches[idx];
        const name = (row[2] || '').trim();
        const email = (row[3] || '').trim();
        const maxStudents = parseInt(row[4]) || 0;
        const notes = (row[6] || '').trim() || null;

        let familyId = match.familyId;

        // Create new family if no match
        if (!familyId) {
          // Split name: try "First Last" format
          const nameParts = name.split(/\s+/);
          const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : name;
          const firstName = nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : name;

          const { data, error } = await supabase.from('families').insert({
            first_name: firstName,
            last_name: lastName,
            combined_name: name,
            email: email,
            status: 'Active',
          }).select('id').single();

          if (error) throw new Error(`Failed to create family "${name}": ${error.message}`);
          familyId = data.id;
          created++;
        }

        // Check if availability already exists for this family + semester
        const { data: existing } = await supabase
          .from('family_availability')
          .select('id')
          .eq('family_id', familyId)
          .eq('semester_id', selectedSemesterId)
          .maybeSingle();

        if (existing) {
          // Update existing
          const { error } = await supabase
            .from('family_availability')
            .update({
              max_students: maxStudents,
              preferences_notes: notes,
              updated_at: new Date().toISOString(),
            })
            .eq('id', existing.id);
          if (error) throw new Error(`Failed to update availability: ${error.message}`);
          updated++;
        } else {
          // Insert new
          const { error } = await supabase
            .from('family_availability')
            .insert({
              family_id: familyId,
              semester_id: selectedSemesterId,
              max_students: maxStudents,
              preferences_notes: notes,
            });
          if (error) throw new Error(`Failed to create availability: ${error.message}`);
        }
      }

      const parts = [`Successfully imported ${selectedRows.length} availability records!`];
      if (created > 0) parts.push(`${created} new families created.`);
      if (updated > 0) parts.push(`${updated} existing records updated.`);
      setStatus(parts.join(' '));
      setSelected(new Set());
      onImportComplete();
    } catch (err: any) {
      setStatus(`Import error: ${err.message}`);
    } finally {
      setIsImporting(false);
    }
  };

  const getMatchLabel = (match: RowMatchInfo) => {
    if (match.matchType === 'email') {
      const family = families.find(f => f.id === match.familyId);
      return { text: `Matched: ${family?.combined_name || 'Unknown'}`, color: '#28a745', bg: '#d4edda' };
    }
    if (match.matchType === 'name') {
      const family = families.find(f => f.id === match.familyId);
      return { text: `Name match: ${family?.combined_name || 'Unknown'}`, color: '#856404', bg: '#fff3cd' };
    }
    return { text: 'New family', color: '#0c5460', bg: '#d1ecf1' };
  };

  const cellStyle: React.CSSProperties = {
    padding: '6px 10px',
    borderBottom: '1px solid #444',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '200px',
  };

  return (
    <div style={{ marginBottom: '30px' }}>
      <h3>Import Family Availability from Google Sheets</h3>

      {/* Sheet URL input */}
      <div style={{ marginBottom: '12px' }}>
        <label style={{ display: 'block', color: '#ccc', marginBottom: '4px', fontSize: '0.9em' }}>
          Family Availability Sheet URL
        </label>
        <input
          type="text"
          value={sheetUrl}
          onChange={e => handleUrlChange(e.target.value)}
          placeholder="https://docs.google.com/spreadsheets/d/..."
          style={{
            width: '100%',
            padding: '8px',
            backgroundColor: '#333',
            color: 'white',
            border: '1px solid #555',
            borderRadius: '4px',
            fontSize: '0.9em',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Auth + Fetch controls */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
        {!isAuthenticated && (
          <button
            onClick={handleAuth}
            style={{
              padding: '8px 16px',
              backgroundColor: '#4285f4',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9em',
            }}
          >
            Connect Google Sheets
          </button>
        )}

        {isAuthenticated && (
          <>
            <label style={{ color: '#ccc', fontSize: '0.9em' }}>Last</label>
            <input
              type="number"
              value={lastN}
              onChange={e => setLastN(Math.max(1, parseInt(e.target.value) || 1))}
              min={1}
              style={{
                width: '60px',
                padding: '6px',
                backgroundColor: '#333',
                color: 'white',
                border: '1px solid #555',
                borderRadius: '4px',
                textAlign: 'center',
              }}
            />
            <label style={{ color: '#ccc', fontSize: '0.9em' }}>rows</label>
            <button
              onClick={handleFetch}
              disabled={isFetching}
              style={{
                padding: '8px 16px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isFetching ? 'not-allowed' : 'pointer',
                opacity: isFetching ? 0.7 : 1,
                fontSize: '0.9em',
              }}
            >
              {isFetching ? 'Fetching...' : 'Fetch Rows'}
            </button>
          </>
        )}
      </div>

      {/* Status message */}
      {status && (
        <p style={{
          color: status.includes('error') || status.includes('Error') || status.includes('Invalid')
            ? '#ff6b6b' : status.includes('Successfully') ? '#28a745' : '#ccc',
          fontSize: '0.9em',
          margin: '8px 0',
        }}>
          {status}
        </p>
      )}

      {/* Results table */}
      {rows.length > 0 && (
        <>
          <div style={{ overflowX: 'auto', marginBottom: '12px', border: '1px solid #444', borderRadius: '4px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85em' }}>
              <thead>
                <tr style={{ backgroundColor: '#333', color: 'white' }}>
                  <th style={{ ...cellStyle, width: '30px' }}>
                    <input
                      type="checkbox"
                      checked={selected.size === rows.length}
                      onChange={toggleAll}
                    />
                  </th>
                  <th style={cellStyle}>Submitted</th>
                  <th style={cellStyle}>Name</th>
                  <th style={cellStyle}>Email</th>
                  <th style={cellStyle}># Students</th>
                  <th style={cellStyle}>Comments</th>
                  <th style={cellStyle}>Match</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const match = rowMatches[i];
                  const label = match ? getMatchLabel(match) : null;
                  return (
                    <tr
                      key={i}
                      onClick={() => toggleRow(i)}
                      style={{
                        backgroundColor: selected.has(i) ? '#c8e6c9' : '#f5f5f5',
                        color: '#222',
                        cursor: 'pointer',
                      }}
                    >
                      <td style={cellStyle}>
                        <input
                          type="checkbox"
                          checked={selected.has(i)}
                          onChange={() => toggleRow(i)}
                          onClick={e => e.stopPropagation()}
                        />
                      </td>
                      <td style={cellStyle}>{row[0] || ''}</td>
                      <td style={cellStyle}>{row[2] || ''}</td>
                      <td style={cellStyle}>{row[3] || ''}</td>
                      <td style={{ ...cellStyle, textAlign: 'center' }}>{row[4] || '0'}</td>
                      <td style={{ ...cellStyle, maxWidth: '250px' }} title={row[6] || ''}>
                        {row[6] || ''}
                      </td>
                      <td style={cellStyle}>
                        {label && (
                          <span style={{
                            fontSize: '0.75em',
                            backgroundColor: label.bg,
                            color: label.color,
                            padding: '2px 6px',
                            borderRadius: '3px',
                            whiteSpace: 'nowrap',
                          }}>
                            {label.text}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <button
            onClick={handleImport}
            disabled={isImporting || selected.size === 0}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isImporting || selected.size === 0 ? 'not-allowed' : 'pointer',
              opacity: isImporting || selected.size === 0 ? 0.7 : 1,
              fontSize: '0.95em',
              fontWeight: 'bold',
            }}
          >
            {isImporting
              ? 'Importing...'
              : `Import Availability for ${selected.size} Famil${selected.size !== 1 ? 'ies' : 'y'}`}
          </button>
        </>
      )}
    </div>
  );
};
